import { Box } from "@rebass/grid";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import { useDebounce } from "@rentiohq/shared-frontend/dist/hooks/useDebounce";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import {
  ERevenuesInvoicedStatus,
  ERevenuesPaymentStatus,
} from "@rentiohq/shared-frontend/dist/reduxV2/exportFile";
import * as paymentRequestActions from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.actions";
import * as paymentRequestSelectors from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.selectors";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { EPaymentOrderType } from "@rentiohq/shared-frontend/dist/types/payment.types";
import {
  EPaymentRequestStatus,
  IPaymentRequest,
} from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import {
  endOfMonth,
  startOfMonth,
} from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  formatCurrency,
  getAmountWithoutVat,
} from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  canCreateInvoice,
  getPaymentOrders,
} from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import {
  AddressCell,
  Button,
  Card,
  ContactFetchListItem,
  DataTable,
  EFilterType,
  ESpacings,
  EmptyState,
  Filters,
  Loading,
  Lozenge,
  Pagination,
  Stack,
  Switch,
  TSortDirection,
  TextStyle,
  TruncateTooltip,
  getRangeForQuickFilter,
  translateDateRange,
} from "@rentiohq/web-shared/dist/components";
import { useMultiselect } from "@rentiohq/web-shared/dist/hooks/useMultiselect";
import { IAction } from "@rentiohq/web-shared/dist/types";
import utils from "@rentiohq/web-shared/dist/utils";
import { sum } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { StringParam } from "serialize-query-params";
import useDeepCompareEffect from "use-deep-compare-effect";
import { ts } from "../../../../services";
import { createTitle } from "../../../../utils/payment";
import {
  EPaymentQuickFilters,
  ONE_TIME_PAYMENT_TYPES,
  RECURRING_PAYMENT_TYPES,
} from "../../Revenues.types";
import {
  ERevenueType,
  getFilterQueryV2,
  getSimpleStatus,
} from "../../Revenues.utils";
import { ExternalInvoiceDetailModal } from "./../../components/ExternalInvoiceDetailModal";
import * as S from "./../../Revenues.styled";

const SELECTION_LIMIT = 500;

interface IProps {
  type: ERevenueType;
  onShowPaymentRequest: (params: { id: number }) => void;
}

enum EFilter {
  PaymentStatus = "paymentStatus",
  InvoicesStatus = "invoicesStatus",
  PaymentTypeRecurring = "paymentTypeRecurring",
  PaymentTypeOneOff = "paymentTypeOneOff",
  PaymentDateQuick = "paymentDateQuick",
  PaymentDateRange = "paymentDateRange",
}

interface IFilter {
  paymentStatus: ERevenuesPaymentStatus[];
  invoicesStatus: ERevenuesInvoicedStatus[];
  paymentTypeRecurring: EPaymentOrderType[];
  paymentTypeOneOff: EPaymentOrderType[];
  paymentDateQuick: EPaymentQuickFilters | undefined;
  paymentDateRange: [Date | null, Date | null] | undefined;
}

const orderMap = [
  "requestPayerAccountId",
  undefined,
  undefined,
  undefined,
  "startedAt",
  undefined,
  undefined,
  undefined,
  undefined,
];

export const RevenueTabList = (props: IProps) => {
  const { type, onShowPaymentRequest } = props;
  const [queryParams] = useQueryParams({
    invoiceStatus: StringParam,
  });

  const initialFilters = React.useMemo(() => {
    if (queryParams?.invoiceStatus) {
      return {
        paymentStatus: [],
        invoicesStatus: [queryParams.invoiceStatus as ERevenuesInvoicedStatus],
        paymentTypeRecurring: [],
        paymentTypeOneOff: [],
        paymentDateQuick: undefined,
        paymentDateRange: undefined,
      };
    }
    return {
      paymentStatus: [],
      invoicesStatus: [],
      paymentTypeRecurring: [],
      paymentTypeOneOff: [],
      paymentDateQuick: undefined,
      paymentDateRange: undefined,
    };
  }, []);

  const [filters, setFilters] = React.useState<IFilter>(initialFilters);

  const [order, setOrder] = React.useState<{
    field: string;
    method: TSortDirection;
  }>({
    field: "startedAt",
    method: "DESC",
  });

  const [page, setPage] = React.useState(1);

  const [queryInput, setQueryInput] = React.useState<string>("");
  const [queryDebounced] = useDebounce(queryInput);

  const [isCreatingInvoice, setIsCreatingInvoice] = React.useState(false);
  const {
    selectedIds,
    onSelectIds,
    onDeselectIds,
    onSelectId,
    onDeselectId,
    onDeselectAll,
  } = useMultiselect<number>([], SELECTION_LIMIT);
  const [externalInvoiceDetailModalId, setExternalInvoiceDetailModalId] =
    React.useState<string>();
  const [appliedFilter, setAppliedFilter] = React.useState<any>({
    page: 1,
    limit: CONFIG.DEFAULT_FETCH_LIMIT,
  });

  const dispatch = useDispatch();

  const { contactMeMaster, user, broker, isBroker } = authHooks.useSelf();
  const hasInvoicing = broker?.features.includes(EBrokerFeature.Invoicing);

  const getQuery = () => {
    const { page, limit, filter: filterQuery, sort } = appliedFilter;
    return {
      limit,
      page,
      sort,
      customFilters: ["FUTURE_REVENUES"],
      filter: {
        ...filterQuery,
      },
      search: queryDebounced.length === 0 ? undefined : queryDebounced,
    };
  };

  const {
    items: paymentRequests,
    isFetching,
    fetchError,
    totalItems,
    totalPages = 1,
  } = useSelector((state: IPartialRootState) =>
    paymentRequestSelectors.getPaged(state, getQuery()),
  ) || {};

  // Data
  const fetchData = () => {
    const { filter } = appliedFilter;
    if (!filter) {
      return;
    }

    dispatch(
      paymentRequestActions.getPagedStart.getAction({
        query: getQuery(),
      }),
    );
  };

  useDeepCompareEffect(() => {
    if (!contactMeMaster) {
      return;
    }

    const {
      paymentStatus,
      invoicesStatus,
      paymentDateQuick,
      paymentDateRange,
      paymentTypeRecurring,
      paymentTypeOneOff,
    } = filters;

    let dateRangeForPayment: { [key: string]: any }[] = [];

    if (paymentDateRange) {
      const start = paymentDateRange[0];
      const end = paymentDateRange[1];

      if (start) {
        dateRangeForPayment.push({
          startedAt: { gte: startOfMonth(start) },
        });
      }

      if (end) {
        dateRangeForPayment.push({
          startedAt: { lte: endOfMonth(end) },
        });
      }
    } else if (
      paymentDateQuick &&
      paymentDateQuick !== EPaymentQuickFilters.DateRange
    ) {
      // @ts-ignore
      const range = getRangeForQuickFilter(paymentDateQuick, "startedAt");
      if (range) dateRangeForPayment = range;
    }

    setAppliedFilter(
      getFilterQueryV2({
        type,
        query: queryDebounced,
        page: 1,
        order,
        paymentStatus,
        invoicesStatus,
        contactMeMaster,
        dateRangeForPayment,
        paymentOrderWheres:
          paymentTypeRecurring?.length > 0 || paymentTypeOneOff?.length > 0
            ? {
                type: {
                  in: [...paymentTypeRecurring, ...paymentTypeOneOff],
                },
              }
            : undefined,
      }),
    );
    setPage(1);
  }, [type, queryDebounced, order, contactMeMaster, filters]);

  React.useEffect(() => {
    setAppliedFilter((prevFilter: any) => {
      const prevPage = prevFilter?.page || 1;

      if (prevPage !== page) {
        return {
          ...prevFilter,
          page,
        };
      }

      return prevFilter;
    });
  }, [page]);

  React.useEffect(() => {
    fetchData();
  }, [appliedFilter]);

  const handleMarkInvoiced = (id: number, value: boolean) => {
    dispatch(
      paymentRequestActions.invoicedAtStart.getAction({
        id,
        invoicedAt: value ? new Date() : null,
      }),
    );
  };

  const handleCreateInvoiceWithConfirm = (params: {
    paymentRequestIds: number[];
  }) => {
    const { paymentRequestIds } = params;

    confirm({
      title: getLocalizedText("revenues.create_invoice.confirm.title"),
      primaryActions: [
        {
          title: getLocalizedText("revenues.create_invoice.cta.without_email"),
          onPress: () => {
            handleCreateInvoice({
              paymentRequestIds,
              automaticallySendToClient: false,
            });
          },
        },
        {
          title: getLocalizedText("revenues.create_invoice.cta.with_email"),
          onPress: () => {
            handleCreateInvoice({
              paymentRequestIds,
              automaticallySendToClient: true,
            });
          },
        },
      ],
    });
  };

  const handleCreateInvoice = async (params: {
    paymentRequestIds: number[];
    automaticallySendToClient: boolean;
  }) => {
    const { paymentRequestIds, automaticallySendToClient } = params;

    try {
      setIsCreatingInvoice(true);

      await api.post("/external-invoice", {
        isV2: true,
        data: {
          paymentRequestIds,
          automaticallySendToClient,
        },
      });

      showAlert({
        type: "success",
        message: getLocalizedText("revenues.create_invoice.success.title"),
        content: getLocalizedText("revenues.create_invoice.success.message"),
      });

      onDeselectAll();
      fetchData();
      setIsCreatingInvoice(false);
    } catch (error) {
      setIsCreatingInvoice(false);

      showAlert({ error, type: "error" });
    }
  };

  const renderStatus = (paymentRequest: IPaymentRequest) => {
    const [simpleStatus, variation] = getSimpleStatus(paymentRequest.status);

    if (paymentRequest.status === EPaymentRequestStatus.Partial) {
      return (
        <Lozenge appearance={variation}>
          {getLocalizedText("revenues.payment_status.partial", {
            amount: formatCurrency(paymentRequest.amount),
          })}
        </Lozenge>
      );
    }

    return (
      <Lozenge appearance={variation}>
        {ts.revenuesPaymentStatusLabel({
          extra: { isBroker, key: simpleStatus },
        })}
      </Lozenge>
    );
  };

  const handleFiltersQueryChange = (value: string) => {
    setQueryInput(value);
    setPage(1);
  };

  const handleQueryValueRemove = () => setQueryInput("");

  const handleFiltersClearAll = React.useCallback(() => {
    handleQueryValueRemove();
  }, [handleQueryValueRemove]);

  const handleSort = (headingIndex: number, direction: TSortDirection) => {
    const sortItem = orderMap[headingIndex];
    if (!sortItem) {
      return;
    }

    setOrder({ field: sortItem, method: direction });
  };

  const handlePageClick = ({ selected }: any) => {
    setPage(selected + 1);
  };

  const handleSelectFilter = (key: EFilter, value: any) => {
    setFilters(prevState => ({ ...prevState, [key]: value }));
  };

  const initialPage = Math.max(0, page - 1);

  const renderFilter = () => {
    const {
      paymentStatus,
      invoicesStatus,
      paymentDateQuick,
      paymentDateRange,
      paymentTypeRecurring,
      paymentTypeOneOff,
    } = filters;
    return (
      <Box mb={ESpacings.loose}>
        <Filters
          queryValue={queryInput}
          queryPlaceholder={ts.revenuesFilterQueryPlaceholder()}
          onQueryChange={handleFiltersQueryChange}
          onQueryClear={handleQueryValueRemove}
          onClearAll={handleFiltersClearAll}
          filterConfigs={[
            {
              label: ts.revenuesFilterLabel({
                extra: { isBroker, key: "payment_status" },
              }),
              groupKey: "paymentStatus",
              filters: [
                {
                  type: EFilterType.MultiSelect,
                  filterKey: "paymentStatus",
                  options: [
                    ERevenuesPaymentStatus.Unpaid,
                    ERevenuesPaymentStatus.Pending,
                    ERevenuesPaymentStatus.Paid,
                  ],
                  values: paymentStatus,
                  onChange: keys =>
                    handleSelectFilter(EFilter.PaymentStatus, keys),
                  onRemove: (key: string) =>
                    handleSelectFilter(
                      EFilter.PaymentStatus,
                      paymentStatus.filter(ps => ps !== key),
                    ),
                  translate: value =>
                    ts.revenuesPaymentStatusLabel({
                      extra: { isBroker, key: value as ERevenuesPaymentStatus },
                    }),
                },
              ],
            },
            {
              label: ts.revenuesFilterLabel({
                extra: { isBroker, key: "invoiced" },
              }),
              groupKey: "invoiced",
              filters: [
                {
                  type: EFilterType.MultiSelect,
                  filterKey: "invoiced",
                  options: [
                    ERevenuesInvoicedStatus.Invoiced,
                    ERevenuesInvoicedStatus.NotInvoiced,
                  ],
                  values: invoicesStatus,
                  onChange: keys =>
                    handleSelectFilter(EFilter.InvoicesStatus, keys),
                  onRemove: (key: string) =>
                    handleSelectFilter(
                      EFilter.InvoicesStatus,
                      invoicesStatus.filter(is => is !== key),
                    ),
                  translate: value =>
                    ts.revenuesInvoicedLabel({
                      extra: {
                        isBroker,
                        key: value as ERevenuesInvoicedStatus,
                      },
                    }),
                },
              ],
            },
            {
              label: getLocalizedText(
                "payment_history.filter.payment_type.title",
              ),
              groupKey: "payment_type",
              filters: [
                {
                  label: getLocalizedText(
                    "payment_history.filter.payment_type.section.recurring.title",
                  ),
                  filterKey: "payment_type_recurring",
                  type: EFilterType.MultiSelect,
                  values: paymentTypeRecurring,
                  options: RECURRING_PAYMENT_TYPES,
                  onChange: keys =>
                    handleSelectFilter(EFilter.PaymentTypeRecurring, [...keys]),
                  onRemove: (key: string) =>
                    handleSelectFilter(
                      EFilter.PaymentTypeRecurring,
                      paymentTypeRecurring.filter(pt => pt !== key),
                    ),
                  translate: (value: any) =>
                    getLocalizedText(
                      `payment.payment_order.type.${value}`.toLowerCase(),
                    ),
                },
                {
                  label: getLocalizedText(
                    "payment_history.filter.payment_type.section.one_off.title",
                  ),
                  filterKey: "payment_type_one_off",
                  type: EFilterType.MultiSelect,
                  values: paymentTypeOneOff,
                  options: ONE_TIME_PAYMENT_TYPES,
                  onChange: keys =>
                    handleSelectFilter(EFilter.PaymentTypeOneOff, [...keys]),
                  onRemove: (key: string) =>
                    handleSelectFilter(
                      EFilter.PaymentTypeOneOff,
                      paymentTypeOneOff.filter(pt => pt !== key),
                    ),

                  translate: (value: any) =>
                    getLocalizedText(
                      `payment.payment_order.type.${value}`.toLowerCase(),
                    ),
                },
              ],
            },
            {
              label: getLocalizedText(
                "payment_history.filter.payment_date.title",
              ),
              groupKey: "payment_date",
              filters: [
                {
                  label: getLocalizedText(
                    "payment_history.filter.payment_date.section.quick.title",
                  ),
                  filterKey: "payment_date_quick",
                  type: EFilterType.SingleSelect,
                  values: paymentDateQuick ? [paymentDateQuick] : undefined,
                  options: Object.values(EPaymentQuickFilters),
                  onChange: newValues => {
                    handleSelectFilter(
                      EFilter.PaymentDateQuick,
                      newValues?.[0],
                    );
                    handleSelectFilter(EFilter.PaymentDateRange, undefined);
                  },
                  showInAppliedFilters: newValues =>
                    newValues?.[0] !== EPaymentQuickFilters.DateRange,
                  onRemove: () =>
                    handleSelectFilter(EFilter.PaymentDateQuick, undefined),
                  translate: (value: any) =>
                    getLocalizedText(
                      `payment.payment_order.type.${value}`.toLowerCase(),
                    ),
                },
                {
                  label: getLocalizedText(
                    "payment_history.filter.payment_date.section.date_range.title",
                  ),
                  filterKey: "payment_date_range",
                  type: EFilterType.MonthRange,
                  minStartDate: new Date(2019, 0, 1),
                  maxStartDate: new Date(),
                  minEndDate: new Date(2019, 0, 1),
                  maxEndDate: new Date(),
                  values: paymentDateRange
                    ? {
                        start:
                          typeof paymentDateRange[0] === "string"
                            ? new Date(paymentDateRange[0])
                            : paymentDateRange[0],
                        end:
                          typeof paymentDateRange[1] === "string"
                            ? new Date(paymentDateRange[1])
                            : paymentDateRange[1],
                      }
                    : undefined,
                  onChange: (newValues: {
                    start: Date | null;
                    end: Date | null;
                  }) => {
                    handleSelectFilter(EFilter.PaymentDateRange, [
                      newValues.start,
                      newValues.end,
                    ]);
                    handleSelectFilter(
                      EFilter.PaymentDateQuick,
                      EPaymentQuickFilters.DateRange,
                    );
                  },
                  onRemove: () =>
                    handleSelectFilter(EFilter.PaymentDateRange, undefined),
                  translate: value => translateDateRange(value),
                },
              ],
            },
          ]}
        />
      </Box>
    );
  };

  if (fetchError) {
    return (
      <Card>
        {renderFilter()}
        <p>{ts.fetchError()}</p>
      </Card>
    );
  }

  return (
    <>
      <Card>
        {renderFilter()}

        {selectedIds && selectedIds.length > 0 && (
          <>
            <Stack vertical={true} spacing="extraTight">
              <Button
                isSubmitting={isCreatingInvoice}
                onClick={() => {
                  handleCreateInvoiceWithConfirm({
                    paymentRequestIds: selectedIds,
                  });
                }}
                appearance="primary"
                iconAfter={
                  <S.SelectedCount>
                    <div>{selectedIds.length}</div>
                  </S.SelectedCount>
                }
              >
                {getLocalizedText("revenues.create_invoice")}
              </Button>

              <Button
                onClick={() => onDeselectAll()}
                appearance="link"
                isDisabled={isCreatingInvoice}
              >
                {getLocalizedText("system.clear_selection", {
                  value: `${selectedIds.length}`,
                })}
              </Button>
            </Stack>
            <Spacer weight={ESpacerWeight.W16} />
          </>
        )}

        {paymentRequests && paymentRequests.length === 0 && !isFetching && (
          <EmptyState heading={ts.emptyStateSearchHeading()} icon="archive">
            {ts.emptyStateSearchContent()}
          </EmptyState>
        )}

        {paymentRequests && paymentRequests.length > 0 && (
          <DataTable
            columnContentTypes={[
              "text",
              "text",
              "text",
              "text",
              "text",
              "numeric",
              "text",
              "text",
              "action",
            ]}
            horizontalSpacing="tight"
            multiSelect={hasInvoicing}
            selectedIds={selectedIds}
            onSelectPage={(ids: (number | string)[]) => {
              onSelectIds(ids as number[]);
            }}
            onDeselectPage={(ids: (number | string)[]) => {
              onDeselectIds(ids as number[]);
            }}
            onSelectRow={(id: number | string) => {
              onSelectId(id as number);
            }}
            onDeselectRow={(id: number | string) => {
              onDeselectId(id as number);
            }}
            headings={[
              getLocalizedText(`revenues.client.${type}.heading`),
              getLocalizedText("revenues.payee.heading"),
              ts.revenuesPropertyHeading(),
              ts.revenuesCostsHeading(),
              ts.revenuesPayDateHeading(),
              ts.revenuesAmountHeading(),
              ts.revenuesPaymentStatusHeading(),
              ts.revenuesProcessedHeading({ extra: { isBroker } }),
              "",
            ]}
            sortable={orderMap.map(x => !!x)}
            sortColumnIndex={orderMap.indexOf(order.field)}
            sortDirection={order.method}
            onSort={handleSort}
            onRowClick={(index: number) => {
              const item = paymentRequests[index];
              if (!item) {
                return;
              }

              onShowPaymentRequest({ id: item.id });
            }}
            rows={paymentRequests.map(paymentRequest => {
              const paymentOrders = getPaymentOrders(paymentRequest);

              const amount = paymentRequest.originalAmount;
              const amountWithoutVat = sum(
                paymentRequest.paymentRequestItems.map(item => {
                  return getAmountWithoutVat(
                    item.amount,
                    item.paymentOrder.vat / 100,
                  );
                }),
              );
              const showAmountWithoutVat = amountWithoutVat < amount;

              const handleInvoiceSwitch = (value: boolean) => {
                handleMarkInvoiced(paymentRequest.id, value);
              };

              const actions: IAction[] = [];

              actions.push({
                content: ts.paymentsFollowUpCardActionsDetailPaymentRequest(),
                onClick: () => {
                  onShowPaymentRequest({ id: paymentRequest.id });
                },
              });

              if (canCreateInvoice({ paymentRequest, broker, user })) {
                actions.push({
                  content: getLocalizedText("revenues.create_invoice"),
                  onClick: () => {
                    handleCreateInvoiceWithConfirm({
                      paymentRequestIds: [paymentRequest.id],
                    });
                  },
                });
              }

              if (paymentRequest.externalInvoiceId) {
                actions.push({
                  content: getLocalizedText("revenues.view_invoice"),
                  onClick: () => {
                    if (!paymentRequest.externalInvoiceId) {
                      return;
                    }

                    setExternalInvoiceDetailModalId(
                      paymentRequest.externalInvoiceId,
                    );
                  },
                });
              } else {
                if (paymentRequest.invoicedAt) {
                  actions.push({
                    content: ts.revenuesActionMarkUnprocessed({
                      extra: { isBroker },
                    }),
                    onClick: () => handleMarkInvoiced(paymentRequest.id, false),
                  });
                } else {
                  actions.push({
                    content: ts.revenuesActionMarkProcessed({
                      extra: { isBroker },
                    }),
                    onClick: () => handleMarkInvoiced(paymentRequest.id, true),
                  });
                }
              }

              return {
                id: paymentRequest.id,
                multiSelectDisabled: !canCreateInvoice({
                  paymentRequest,
                  broker,
                  user,
                }),
                content: [
                  <TruncateTooltip width={170}>
                    {getName(paymentRequest.payerAccount)}
                  </TruncateTooltip>,

                  <ContactFetchListItem
                    accountId={paymentRequest.requestPayeeAccountId}
                    renderContact={getName}
                    renderAccount={getName}
                  />,

                  <AddressCell
                    propertyId={paymentRequest.requestPropertyId}
                    address={paymentRequest.propertyAddress}
                  />,

                  <TruncateTooltip width={220}>
                    {createTitle({ paymentOrders })}
                  </TruncateTooltip>,

                  <TextStyle>
                    <div style={{ whiteSpace: "nowrap" }}>
                      {utils.date.format(paymentRequest.startedAt)}
                      {paymentRequest.startedAt !== paymentRequest.endedAt && (
                        <TextStyle variation="subdued" element="div">
                          {utils.date.format(paymentRequest.endedAt)}
                        </TextStyle>
                      )}
                    </div>
                  </TextStyle>,

                  <TextStyle
                    variation="code"
                    key={`${paymentRequest.id}-amount`}
                  >
                    <div style={{ whiteSpace: "nowrap" }}>
                      {formatCurrency(amount)}
                      {showAmountWithoutVat && (
                        <TextStyle
                          variation="subdued"
                          size="small"
                          element="div"
                        >
                          {"("}
                          {getLocalizedText("system.amount_without_vat", {
                            amount: formatCurrency(amountWithoutVat),
                          })}
                          {")"}
                        </TextStyle>
                      )}
                    </div>
                  </TextStyle>,

                  renderStatus(paymentRequest),

                  // paymentRequest.invoicedAt ? utils.date.format(paymentRequest.invoicedAt) : '-',

                  <Switch
                    key={`${paymentRequest.id}-processed`}
                    isChecked={
                      !!paymentRequest.invoicedAt ||
                      !!paymentRequest.externalInvoiceId
                    }
                    onChange={handleInvoiceSwitch}
                    isDisabled={!!paymentRequest.externalInvoiceId}
                  />,
                ],
                actions,
              };
            })}
            totalPages={totalPages}
          />
        )}

        {paymentRequests && paymentRequests.length === 0 && isFetching && (
          <Loading asDots={true} />
        )}

        {!!totalItems && totalPages > 1 && (
          <Pagination
            initialPage={initialPage}
            pageCount={totalPages}
            onPageChange={handlePageClick}
          />
        )}
      </Card>

      {externalInvoiceDetailModalId && (
        <ExternalInvoiceDetailModal
          externalInvoiceId={externalInvoiceDetailModalId}
          onClose={() => {
            setExternalInvoiceDetailModalId(undefined);
          }}
        />
      )}
    </>
  );
};
